/**
 * 邮件黑名单管理页面
 *
 * 管理被屏蔽的发件人邮箱地址列表。被屏蔽地址的新邮件将自动移入垃圾箱。
 * 支持添加/删除黑名单条目，以及将规则应用到现有（存量）邮件。
 *
 * 自定义元素：`crm-email-blacklist-page`
 * 路由：`/crm/email-blacklist`
 *
 * 主要功能：
 * - 查看黑名单列表
 * - 添加新邮箱到黑名单（模态框输入）
 * - 从黑名单移除邮箱
 * - "应用到存量"：将当前黑名单规则应用到收件箱中的历史邮件
 *
 * 依赖模块：
 * - `emailBlacklistApi`：黑名单 API
 * - `showConfirm`：确认弹窗
 * - `toast`：消息提示
 */
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { BaseElement } from '../base-element.js';
import { toast } from '../components/toast.js';
import { showConfirm } from '../components/confirm-dialog.js';
import { emailBlacklistApi } from '../api.js';
import { isNetworkError } from '../utils/index.js';

/**
 * 黑名单条目数据结构
 */
interface BlacklistItem {
  /** 黑名单记录 ID */
  id: number;
  /** 被屏蔽的邮箱地址 */
  email_address: string;
  /** 创建时间（Unix 时间戳，秒） */
  created_at?: number;
}

@customElement('crm-email-blacklist-page')
export class CrmEmailBlacklistPage extends BaseElement {
  static styles = css`
    :host { display: block; height: 100%; }
  `;

  /** 黑名单条目列表 */
  @state() private items: BlacklistItem[] = [];
  /** 模态框中的邮箱输入值 */
  @state() private newEmail = '';
  /** 控制添加黑名单模态框的显示/隐藏 */
  @state() private showModal = false;
  /**
   * 遮罩点击追踪标志
   * 用于区分点击的是遮罩背景还是模态框内容：
   * mousedown 记录是否点在遮罩上，click 时据此判断是否关闭。
   */
  private _backdropMousedown = false;
  /** 列表请求序号，仅允许最后一次刷新更新页面。 */
  private _listRequestId = 0;

  /**
   * 组件挂载时触发，加载黑名单列表
   */
  connectedCallback() {
    super.connectedCallback();
    this.loadList();
  }

  /**
   * 从后端加载黑名单列表
   *
   * 静默处理错误——黑名单加载失败不弹 toast，避免在页面加载时打扰用户。
   *
   * @returns Promise<void>
   */
  private async loadList() {
    const requestId = ++this._listRequestId;
    try {
      const items = await emailBlacklistApi.list();
      if (requestId === this._listRequestId) this.items = items;
    } catch { /* ignore */ }
  }

  /**
   * 处理添加黑名单操作
   *
   * 将输入的邮箱标准化（trim + toLowerCase），调用后端添加 API，
   * 成功后关闭模态框并刷新列表。
   *
   * @returns Promise<void>
   */
  private async handleAdd() {
    const email = this.newEmail.trim().toLowerCase();
    if (!email) return;
    try {
      await emailBlacklistApi.add(email);
      this.newEmail = '';
      this.showModal = false;
      await this.loadList();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message);
    }
  }

  /** 打开添加黑名单模态框，清空输入 */
  private openModal() { this.newEmail = ''; this.showModal = true; }
  /** 关闭添加黑名单模态框 */
  private closeModal() { this.showModal = false; }

  /**
   * 处理删除黑名单操作
   *
   * 弹出确认弹窗，用户确认后调用后端删除 API 并刷新列表。
   *
   * @param item - 要删除的黑名单条目
   * @returns Promise<void>
   */
  private async handleDelete(item: BlacklistItem) {
    if (!(await showConfirm(`确定从黑名单移除 ${item.email_address}？`))) return;
    try {
      await emailBlacklistApi.remove(item.id);
      await this.loadList();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message);
    }
  }

  /**
   * 处理"应用到存量"操作
   *
   * 将当前黑名单规则应用到收件箱中已有的邮件——所有匹配黑名单地址的邮件
   * 将被移入垃圾箱。弹出确认弹窗后执行后端 API。
   *
   * @returns Promise<void>
   */
  private async handleApply() {
    if (!(await showConfirm('将收件箱中所有匹配黑名单的邮件移入垃圾箱？'))) return;
    try {
      const data = await emailBlacklistApi.apply();
      toast.info(data.message || '执行完成');
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message);
    }
  }

  /**
   * 渲染黑名单管理页面
   *
   * 页面结构：
   * 1. 顶部头部：图标 + 标题 + 屏蔽地址数量 + 操作按钮（应用到存量 / 添加黑名单）
   * 2. 添加黑名单模态框（条件渲染）
   * 3. 黑名单列表（空状态 / 条目列表）
   *
   * @returns Lit 模板
   */
  render() {
    return html`
      <div class="flex flex-col h-full gap-5">
        <!-- 头部区域：图标、标题、数量、操作按钮 -->
        <div class="flex items-center justify-between flex-shrink-0">
          <div class="flex items-center gap-3">
            <span class="inline-flex items-center justify-center w-10 h-10 rounded-full bg-error/10 text-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
            </span>
            <div>
              <h2 class="text-lg font-semibold text-text m-0 leading-tight">黑名单</h2>
              <p class="text-xs text-text-muted m-0">${this.items.length} 个屏蔽地址</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <!-- 仅在有黑名单数据时显示"应用到存量"按钮 -->
            ${this.items.length > 0 ? html`
              <button class="inline-flex items-center gap-1.5 px-4 py-1.5 bg-warning/10 text-warning border border-warning/20 rounded text-sm font-semibold hover:bg-warning hover:text-white transition-all duration-200 cursor-pointer"
                      @click=${this.handleApply}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                应用到存量
              </button>
            ` : ''}
            <!-- 添加黑名单按钮：打开模态框 -->
            <button class="inline-flex items-center gap-2 px-5 py-1.5 bg-primary hover:bg-primary-hover text-white rounded text-sm font-semibold transition-all duration-200 cursor-pointer shadow-sm shadow-primary/20"
                    @click=${this.openModal}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              添加黑名单
            </button>
          </div>
        </div>

        <!-- 添加黑名单模态框（条件渲染：showModal 为 true 时显示） -->
        ${this.showModal ? html`
          <!-- 遮罩层：点击空白处关闭模态框 -->
          <div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50" @mousedown=${(e: Event) => { this._backdropMousedown = e.target === e.currentTarget; }} @click=${() => { if (this._backdropMousedown) this.closeModal(); }}>
            <!-- 模态框内容容器，阻止冒泡避免误关 -->
            <div class="bg-surface rounded-md shadow-xl w-full max-w-md border border-border mx-4 overflow-hidden" @click=${(e: Event) => e.stopPropagation()}>
              <div class="px-6 pt-5 pb-4">
                <h3 class="text-lg font-semibold text-text m-0">添加黑名单</h3>
                <p class="text-sm text-text-muted mt-1 m-0">来自该地址的新邮件将自动移入垃圾箱</p>
              </div>
              <div class="px-6 pb-2">
                <!-- 邮箱输入框：Enter 键直接提交 -->
                <input type="email" placeholder="输入发件人邮箱地址..."
                       .value=${this.newEmail}
                       @input=${(e: Event) => { this.newEmail = (e.target as HTMLInputElement).value; }}
                       @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this.handleAdd(); }}
                       class="w-full px-4 py-3 border border-border rounded-md text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" />
              </div>
              <div class="flex justify-end gap-2 px-6 pb-5 pt-3">
                <button class="px-4 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded transition-colors cursor-pointer"
                        @click=${this.closeModal}>取消</button>
                <!-- 确认按钮在输入为空时禁用 -->
                <button class="px-4 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors cursor-pointer shadow-sm shadow-primary/20"
                        @click=${this.handleAdd} ?disabled=${!this.newEmail.trim()}>确认添加</button>
              </div>
            </div>
          </div>
        ` : ''}

        <!-- 黑名单列表区域 -->
        <div class="flex-1 min-h-0 flex flex-col">
          <div class="bg-surface rounded-md shadow-sm flex flex-col flex-1 min-h-0 overflow-hidden border border-border">
            <!-- 空状态：列表无数据时显示引导文案 -->
            ${this.items.length === 0 ? html`
              <div class="flex-1 flex flex-col items-center justify-center text-text-muted gap-3 p-8">
                <div class="w-16 h-16 rounded-md bg-bg flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="w-8 h-8"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                </div>
                <p class="text-sm font-medium">黑名单为空</p>
                <p class="text-xs">添加发件人邮箱后，新邮件将自动进入垃圾箱</p>
              </div>
            ` : html`
              <!-- 黑名单条目列表：可滚动容器 -->
              <div class="overflow-auto flex-1 min-h-0">
                ${this.items.map((item, i) => html`
                  <!-- 单条黑名单记录：头像 + 邮箱地址 + 创建时间 + 移除按钮 -->
                  <div class="flex items-center justify-between px-5 py-3.5 hover:bg-bg/40 transition-colors duration-150 ${i < this.items.length - 1 ? 'border-b border-border' : ''}">
                    <div class="flex items-center gap-3 min-w-0">
                      <!-- 头像：取邮箱首字母大写显示 -->
                      <span class="inline-flex items-center justify-center w-9 h-9 rounded-full bg-error/5 text-error text-xs font-bold flex-shrink-0">
                        ${item.email_address.charAt(0).toUpperCase()}
                      </span>
                      <div class="min-w-0">
                        <p class="text-sm font-semibold text-text truncate m-0">${item.email_address}</p>
                        <!-- 创建时间（有值时才显示） -->
                        ${item.created_at ? html`<p class="text-xs text-text-muted m-0">${new Date(item.created_at * 1000).toLocaleString('zh-CN', {year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit'})}</p>` : ''}
                      </div>
                    </div>
                    <!-- 移除按钮：点击触发 handleDelete -->
                    <button class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-error/70 hover:text-error hover:bg-error/5 rounded transition-all duration-150 cursor-pointer ml-3 flex-shrink-0"
                            @click=${() => this.handleDelete(item)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      移除
                    </button>
                  </div>
                `)}
              </div>
            `}
          </div>
        </div>
      </div>
    `;
  }
}
